import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { FaTrophy, FaPlay, FaClock } from 'react-icons/fa';
import './MatchCard.css';

const MatchCard = ({ match, onParticipate, isParticipated = false }) => {
    const getMatchStatus = (match) => {
        // Correction : tout en UTC pour éviter les fraudes
        const nowUtc = Date.now(); // UTC
        const matchTimeUtc = Date.parse(match.scheduledTime); // UTC
        const timeDiff = matchTimeUtc - nowUtc;
        const tenMinutesBefore = 10 * 60 * 1000; // 10 minutes en millisecondes
        
        if (timeDiff > tenMinutesBefore) {
            // Match dans plus de 10 minutes - PARTICIPATION OUVERTE
            const hours = Math.floor(timeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            
            if (hours > 0) {
                return `Dans ${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
            } else {
                return `Dans ${minutes}min`;
            }
        } else if (timeDiff > 0) {
            // Match dans moins de 10 minutes - PARTICIPATION FERMÉE
            const minutes = Math.floor(timeDiff / (1000 * 60));
            return `Participation fermée (${minutes}min)`;
        } else if (timeDiff > -2 * 60 * 60 * 1000) { // 2h après
            return 'En cours';
        } else {
            return 'Terminé';
        }
    };

    const isMatchFinished = getMatchStatus(match) === 'Terminé';
    const isParticipationClosed = getMatchStatus(match).includes('Participation fermée');
    const isButtonDisabled = isMatchFinished || isParticipationClosed;

    // Fonction simple pour afficher le score (seulement pour les matchs terminés)
    const renderScore = () => {
        // Seulement afficher le score si le match est terminé ET qu'on a un score
        if (!isMatchFinished || !match.score?.fullTime) {
            return 'VS';
        }
        
        const { home, away } = match.score.fullTime;
        // Vérifier que les scores ne sont pas null
        if (home === null || away === null) {
            return 'VS';
        }
        
        return (
            <div className="final-score">
                <span className="score">{home}</span>
                <span className="separator">-</span>
                <span className="score">{away}</span>
            </div>
        );
    };

    const getCompetitionLogo = (competition) => {
        // Toujours utiliser le logo GigaScore
        return '/gigascore-logo.svg';
    };

    return (
        <motion.div 
            className={`match-card ${isParticipated ? 'participated' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            whileHover={{ scale: 1.02 }}
        >
            <div className="match-header">
                <img 
                    src={getCompetitionLogo(match.competition)}
                    alt={match.competition?.name}
                    className="competition-logo"
                    onError={(e) => {
                        e.target.src = '/images/competitions/default.svg';
                    }}
                />
                <span className="competition-name">
                    {match.competition?.name}
                </span>
                <span className="match-status">
                    {getMatchStatus(match)}
                </span>
            </div>
            
            <div className="teams">
                <div className="team home">
                    <img
                        src={match.homeTeam?.crest || '/images/placeholder_team.svg'}
                        alt={match.homeTeam?.name}
                        onError={(e) => {
                            e.target.src = '/images/placeholder_team.svg';
                        }}
                    />
                    <span>{match.homeTeam?.name}</span>
                </div>
                <div className="vs">
                    {renderScore()}
                </div>
                <div className="team away">
                    <img
                        src={match.awayTeam?.crest || '/images/placeholder_team.svg'}
                        alt={match.awayTeam?.name}
                        onError={(e) => {
                            e.target.src = '/images/placeholder_team.svg';
                        }}
                    />
                    <span>{match.awayTeam?.name}</span>
                </div>
            </div>
            
            <div className="match-time">
                <FaClock className="time-icon" />
                {(() => {
                    const matchDate = new Date(match.scheduledTime);
                    const today = new Date();
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    
                    // Normaliser les dates (sans heure)
                    const matchDay = new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate());
                    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const tomorrowDay = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
                    
                    const timeStr = matchDate.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                    
                    if (matchDay.getTime() === todayDay.getTime()) {
                        return `Aujourd'hui ${timeStr}`;
                    } else if (matchDay.getTime() === tomorrowDay.getTime()) {
                        return `Demain ${timeStr}`;
                    } else {
                        const dateStr = matchDate.toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short'
                        });
                        return `${dateStr} ${timeStr}`;
                    }
                })()}
            </div>
            
            <div className="match-reward">
                <FaTrophy className="reward-icon" />
                <span>1 Go à gagner</span>
            </div>
            
            {isParticipated ? (
                <div className="participated-badge">
                    <span>Déjà participé</span>
                </div>
            ) : (
                <motion.button
                    className="participate-button"
                    onClick={() => onParticipate(match)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isButtonDisabled}
                >
                    <FaPlay className="button-icon" />
                    {isMatchFinished ? 'Match terminé' : 
                     isParticipationClosed ? 'Participation fermée' : 'Participer'}
                </motion.button>
            )}
        </motion.div>
    );
};

MatchCard.propTypes = {
    match: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        scheduledTime: PropTypes.string.isRequired,
        competition: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            code: PropTypes.string
        }),
        homeTeam: PropTypes.shape({
            name: PropTypes.string,
            crest: PropTypes.string
        }),
        awayTeam: PropTypes.shape({
            name: PropTypes.string,
            crest: PropTypes.string
        }),
        score: PropTypes.shape({
            fullTime: PropTypes.shape({
                home: PropTypes.number,
                away: PropTypes.number
            }),
            halfTime: PropTypes.shape({
                home: PropTypes.number,
                away: PropTypes.number
            })
        })
    }).isRequired,
    onParticipate: PropTypes.func.isRequired,
    isParticipated: PropTypes.bool
};

export default MatchCard; 